import {
  faCheckCircle,
  faDoorOpen,
  faTimesCircle,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import queryString from 'query-string';
import React from 'react';
import { useMutation } from 'react-query';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Row, Col, Container, CardBody, CardTitle, Spinner } from 'reactstrap';
import urljoin from 'url-join';

import { AutoFocusHeader } from '../../../_components/auto-focus-header';
import { Button } from '../../../_components/button';
import { useConfig } from '../../../_components/config';
import { ClipboardCopy } from '../../../_components/copy';
import { parseTextWithURL } from '../../../_components/parse-text-with-url';
import { useToastr } from '../../../_components/toastr';

export interface SubmissionResult {
  state: string;
  precertificationId: string;
  submittedAt: string;
  error?: string;
  caution?: string;
  retries?: number;
  processingId?: string;
}

export function UpdateSubmissionResult() {
  const navigate = useNavigate();
  const { api } = useConfig();
  const { precertificationId } = useParams();
  const { search } = useLocation();
  const { sessionId, error, caution, processingId } = queryString.parse(search);
  const toast = useToastr();

  const { isLoading, mutateAsync: signOut } = useMutation({
    mutationFn: async () =>
      axios({
        url: urljoin(api, 'signOut'),
        method: 'post',
        withCredentials: true,
        data: {
          sessionId: sessionId || null,
        },
      }),
    onSuccess: () => {
      navigate('/compliance');
    },
    onError: (err) => {
      toast.error('Failed to return user to compliance page', {
        details: err instanceof Error && err?.message,
      });
    },
  });

  return (
    <Container className="py-4 text-center" fluid>
      <div className="mx-auto my-5 col-md-9 col-lg-9">
        <CardBody className="my-2 mx-1" role="main">
          {error ? (
            <>
              <CardTitle>
                <AutoFocusHeader
                  className="mt-0 mt-sm-3 mb-3 mb-sm-5"
                  id="main-content"
                >
                  Update Failed
                </AutoFocusHeader>
                <FontAwesomeIcon
                  icon={faTimesCircle}
                  size="8x"
                  className="text-red mb-1 mb-sm-5"
                />
              </CardTitle>
              <div className="d-flex justify-content-center">
                <p style={{ maxWidth: '700px' }}>
                  {parseTextWithURL(error as string)}
                </p>
              </div>
              {processingId && (
                <div className="text-muted small mb-2">PID: {processingId}</div>
              )}
            </>
          ) : (
            <>
              <CardTitle>
                <AutoFocusHeader
                  className="mt-0 mt-sm-3 mb-3 mb-sm-5"
                  id="main-content"
                >
                  Update Successful
                </AutoFocusHeader>
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  size="8x"
                  className="text-green mb-1 mb-sm-5"
                />
                <div className="h4 mb-3 fw-bold">
                  Thank you for updating this precertification notification.
                </div>
                {processingId && (
                  <div className="text-muted small mb-2">
                    PID: {processingId}
                  </div>
                )}
              </CardTitle>
              <div>
                <div className="d-flex justify-content-center">
                  <p style={{ maxWidth: '700px' }}>
                    Your precertification notification has been updated
                    successfully. Please proceed with rendering the requested
                    services as clinically appropriate.
                  </p>
                </div>
                {caution && (
                  <div className="d-flex justify-content-center">
                    <p style={{ maxWidth: '700px' }}>
                      <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        color="orange"
                        size="lg"
                        className="me-2"
                      />
                      <span className="fw-bold">PLEASE READ:</span> {caution}
                    </p>
                  </div>
                )}
                Please make note of the Precertification ID number below.
                <Row className="mt-4">
                  <Col md={12} lg={12} className="mb-3">
                    <b>Precertification ID: </b>
                    <span className="text-break">{precertificationId}</span>
                    <ClipboardCopy
                      text={precertificationId ?? ''}
                      className="ms-1 mb-1"
                      label="Copy to clipboard"
                      description="Press enter to copy the precertification ID to the clipboard."
                    />
                  </Col>
                </Row>
              </div>
            </>
          )}
        </CardBody>
        <hr aria-hidden="true" />
        <Container>
          <Row className="d-flex justify-content-center">
            <Col sm={12} className="text-center my-1" md={3}>
              <Button
                aria-description="Press Enter to sign out"
                color="primary"
                disabled={isLoading}
                onClick={async () => {
                  await signOut();
                  toast.success(
                    <span>
                      Signed out. Please request a new session if needed.
                    </span>
                  );
                }}
              >
                Sign Out
                {isLoading ? (
                  <Spinner size="sm" className="ms-2" />
                ) : (
                  <FontAwesomeIcon icon={faDoorOpen} className="ms-2" />
                )}
              </Button>
            </Col>
            <Col className="text-center my-1" sm={12} md={3}>
              <Button
                onClick={() => navigate('/form/new-request')}
                className="me-2"
                color="success"
                aria-label="Create"
                aria-description="Press enter to create a new Precertification"
              >
                Create a New Precertification
              </Button>
            </Col>
            <Col className="text-center my-1" sm={12} md={3}>
              <Button
                onClick={() => navigate('/update')}
                color="primary"
                aria-label="Update"
                aria-description="Press enter to update an existing Precertification"
              >
                Update an Existing Precertification
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    </Container>
  );
}
